import type { CSSProperties, MouseEvent, ReactNode } from "react";
import MenuList from "@mui/material/MenuList";
import MuiMenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";

// antd <Menu>/<Menu.Item> -> MUI MenuList/MenuItem. Used standalone and as a
// Dropdown overlay. antd Menu.onClick({key}) is mapped by tagging each item.

export interface MenuItemType {
  key: string | number;
  label?: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
  danger?: boolean;
  type?: "divider" | "group" | "item";
  onClick?: () => void;
}

export interface MenuProps {
  items?: MenuItemType[];
  onClick?: (info: { key: string }) => void;
  // antd SelectEventHandler passes a richer info object; keep loose for compat.
  onSelect?: (info: any) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
  onDeselect?: (info: any) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
  multiple?: boolean;
  selectedKeys?: string[];
  mode?: "vertical" | "horizontal" | "inline";
  style?: CSSProperties;
  className?: string;
  children?: ReactNode;
}

interface ItemProps {
  disabled?: boolean;
  danger?: boolean;
  icon?: ReactNode;
  onClick?: (e: MouseEvent) => void;
  children?: ReactNode;
  "data-cy"?: string;
  "data-testid"?: string;
}

const Item = ({ disabled, icon, onClick, children, "data-cy": dataCy, "data-testid": dataTestid }: ItemProps) => (
  <MuiMenuItem disabled={disabled} onClick={onClick} data-cy={dataCy} data-testid={dataTestid}>
    {icon ? <span style={{ marginRight: 8, display: "inline-flex" }}>{icon}</span> : null}
    {children}
  </MuiMenuItem>
);

export const Menu = ({ items, onClick, style, className, children }: MenuProps) => (
  <MenuList style={style} className={className}>
    {items
      ? items.map((it) =>
          it.type === "divider" ? (
            <Divider key={it.key} />
          ) : (
            <MuiMenuItem
              key={String(it.key)}
              disabled={it.disabled}
              onClick={() => {
                it.onClick?.();
                onClick?.({ key: String(it.key) });
              }}
            >
              {it.icon}
              <ListItemText sx={it.icon ? { ml: 1 } : undefined}>{it.label}</ListItemText>
            </MuiMenuItem>
          ),
        )
      : children}
  </MenuList>
);

Menu.Item = Item;
Menu.Divider = Divider;

export type { MenuProps as MenuConfigProps };
export default Menu;
